import React, { useState } from "react";
import { isEmpty } from "lodash";
import classNames from "classnames";
import { Flex, Banner, type BannerVariant, Checkbox } from "@jsq/ds/core";
import { FormEmailInput, FormPasswordInput, Form, SubmitButton } from "@jsq/ds/forms";
import { Text } from "@jsq/ds/deprecated";
import { type UnauthenticatedArenaObjectType } from "apps/portal/Portal/common/types";
import { getUnifiedPortalPP, getUnifiedPortalTouActivation } from "common/helpers/url";
import { useQueryParams } from "common/hooks/useQueryParams";
import { LoginCard } from "../../common/LoginCard";
import { defaultSingleManagerPoweredByMessage } from "../../common/LoginCard/constants";
import { UserContext } from "../../common/constants";
import { type SingleSignOnCardProps } from "../../common/SingleSignOnCard/types";
import styles from "../../common/styles.scss";
import { getInfoBannerProps, getPasswordResetBannerProps, getPortalBannerProps } from "./utils";
import { schema } from "./schema";

type LoginFormik = {
  email: string;
  password: string;
};
type Props = {
  handleLoginSubmit: (values: LoginFormik) => Promise<void>;
  email: string;
  password: string;
  onForgotPassword: () => void;
  showPasswordResetSuccessBanner?: boolean;
  showHasManagerAccountBanner?: boolean;
  showActivationLinkBanner?: boolean;
  showHasActiveGlobalAccountBanner?: boolean;
  showContactSupportBanner?: boolean;
  showPortalTouCheckBox?: boolean;
  showPoweredBy?: boolean;
  ssoProps?: SingleSignOnCardProps;
  error?: {
    title?: string;
    description: string | JSX.Element;
  };
  arena?: UnauthenticatedArenaObjectType;
  loginCardTitle?: string;
  portalLinkedGpArenaNames?: string;
  userContext: UserContext;
};

const LoginForm: React.FC<Props> = ({
  handleLoginSubmit,
  email,
  password,
  onForgotPassword,
  showPasswordResetSuccessBanner,
  showHasManagerAccountBanner,
  showActivationLinkBanner,
  showHasActiveGlobalAccountBanner,
  showContactSupportBanner,
  showPortalTouCheckBox,
  ssoProps,
  error,
  arena,
  loginCardTitle,
  showPoweredBy,
  portalLinkedGpArenaNames,
  userContext,
}) => {
  const [acceptTou, setAcceptTou] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const showPasswordResetBanner = showPasswordResetSuccessBanner;
  const passwordResetBannerProps = showPasswordResetBanner
    ? getPasswordResetBannerProps({
        showPasswordResetSuccessBanner,
        passwordResetEmail: email,
      })
    : null;

  const showPortalBanner =
    showHasManagerAccountBanner ||
    showActivationLinkBanner ||
    showHasActiveGlobalAccountBanner ||
    showContactSupportBanner;
  const [params] = useQueryParams();
  const {
    path,
    activation: activationCode,
    arenaId,
    secureDocumentsKey,
  } = params as {
    path: string;
    activation: string;
    arenaId: string;
    secureDocumentsKey: string;
  };
  const portalActivationLink = `networked_portal/join?secureDocumentsKey=${secureDocumentsKey}&arenaId=${arenaId}&path=${
    path || "home"
  }`;
  const portalBannerProps = showPortalBanner
    ? getPortalBannerProps({
        showContactSupportBanner,
        showHasManagerAccountBanner,
        showActivationLinkBanner,
        activationLink: portalActivationLink,
        showHasActiveGlobalAccountBanner,
        currentGpArenaName: arena?.name,
        portalLinkedGpArenaNames,
        gpContactEmail: arena?.contactEmail,
        gpContactPhone: arena?.contactPhone,
      })
    : null;

  const isGpLogin = userContext === UserContext.Arena;
  const buttonText = !isGpLogin && arena?.signInButtonTextColor;
  const buttonBackground = !isGpLogin && arena?.signInButtonBackgroundColor;
  const buttonVariantOn = buttonBackground || buttonText;

  const infoBanner = searchParams.get("banner");
  const infoBannerProps = getInfoBannerProps(infoBanner);

  return (
    <LoginCard
      showPoweredByHeader={showPoweredBy && !isEmpty(arena)}
      poweredByDataTestId="login-step-header"
      poweredByTitle="Sign in with"
      poweredByHelpMessage={defaultSingleManagerPoweredByMessage(arena?.name)}
      showCardTitle={(isEmpty(ssoProps) && isEmpty(arena)) || !showPoweredBy}
      cardTitle={loginCardTitle}
      ssoProps={ssoProps}
      titleDataTestId="login-step-header"
    >
      {showPasswordResetBanner && passwordResetBannerProps && (
        <Banner
          className={classNames(passwordResetBannerProps.className, styles.Login__formBanner)}
          variant={passwordResetBannerProps.variant as BannerVariant}
          title={passwordResetBannerProps.title}
          description={passwordResetBannerProps.description}
          isDescriptionJsx={passwordResetBannerProps.isDescriptionJsx}
          data-cy="password-reset-banner"
        />
      )}
      {showPortalBanner && portalBannerProps && (
        <div data-testid="login-page-unified-portal-banner">
          <Banner
            className={classNames(portalBannerProps.className, styles.Login__formBanner)}
            variant={portalBannerProps.variant as BannerVariant}
            title={portalBannerProps.title}
            description={portalBannerProps.description}
            isDescriptionJsx={portalBannerProps.isDescriptionJsx}
            data-cy="login-page-unified-portal-banner"
          />
        </div>
      )}
      {infoBanner && (
        <Banner
          className="stacked-md"
          variant="primary"
          title={infoBannerProps.title}
          description={infoBannerProps.description}
        />
      )}
      {error && (
        <Banner
          className={styles.Login__formBanner}
          variant="danger"
          title={error.title || "Error"}
          description={error.description}
          data-cy="signin-error"
        />
      )}
      <div className={styles.Login__form}>
        <Form
          submitOnlyChangedValues={false}
          initialValues={{ email, password, showPortalTouCheckBox }}
          validationSchema={schema}
          onSubmit={handleLoginSubmit}
        >
          <FormEmailInput
            name="email"
            className={styles.Login__input}
            data-cy="signin-email-input"
            data-testid="signin-email"
            isFullWidth
          />
          <FormPasswordInput
            name="password"
            placeholder=""
            className={styles.Login__input}
            data-cy="signin-password-input"
            data-testid="signin-password"
            isFullWidth
          />
          <Flex flexDirection="column" className="stacked-md" alignItems="flex-start">
            <Text
              onClick={() => onForgotPassword()}
              className={styles.Login__resetPasswordLink}
              data-cy="reset-link"
            >
              Forgot password?
            </Text>
          </Flex>
          {showPortalTouCheckBox && (
            <>
              <Flex className="stacked-md" data-testid="portal-login-tou-checkbox">
                <Checkbox
                  name="acceptTou"
                  checked={acceptTou}
                  onChange={() => setAcceptTou(!acceptTou)}
                />
                <p className="text-md" data-cy="tou-terms-label">
                  I agree to the{" "}
                  <a
                    // eslint-disable-next-line react/jsx-no-target-blank
                    target="_blank"
                    rel="noopener noreferrer"
                    href={getUnifiedPortalTouActivation(activationCode)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Use
                  </a>
                </p>
              </Flex>
              <p className="stacked-xl" data-cy="user-agreement-disclosure">
                I acknowledge that my personal information will be used and disclosed as set
                forth in the{" "}
                <a
                  // eslint-disable-next-line react/jsx-no-target-blank
                  target="_blank"
                  rel="noopener noreferrer"
                  href={getUnifiedPortalPP(activationCode)}
                >
                  Privacy Policy
                </a>
              </p>
            </>
          )}
          <SubmitButton
            className={classNames("inline-sm", styles.Login__submitButton)}
            data-cy="signin-button"
            isFullWidth
            disabled={showPortalTouCheckBox && !acceptTou}
            variant={buttonVariantOn ? "custom" : "primary"}
            backgroundColor={buttonBackground || undefined}
            textColor={buttonText || undefined}
          >
            Sign in
          </SubmitButton>
        </Form>
      </div>
    </LoginCard>
  );
};

export default LoginForm;
